"use client";
import { resolveAgeMode } from "@/lib/essential-only";
import { syncAdPixels } from "@/lib/ad-pixels";

// B3.4 MARKETPLACE CART — WO-GF-001 Phase-1b, the session cart system. This ports the LIVE cart
// CAPABILITY (legacy snowbus-app `booking.context.js`) onto the NEW backend machinery
// (snowclub-be `POST /api/marketplace/book`) — "port the capability, replace the machinery".
//
// THE FOUNDER RULE this store exists to honour (the exact one the old build violated):
//   signed-out add  →  session-held  →  sign-in  →  DRAFT ORDER exists  →  cart survives
//
//   • signed-out add  — addItem() works with no session; the item lives client-side only.
//   • session-held    — zustand/persist mirrors the store to localStorage ("sc-cart"), so the cart
//                        survives a reload while signed out (live keyed carts per mountain in
//                        localStorage; we key each item by mountain+supplier+product and persist one
//                        store — whistler-only in Phase-1).
//   • sign-in         — signIn() stores the JWT and calls reconcile().
//   • DRAFT ORDER     — reconcile() books each held item via the BE (member price, auth'd),
//                        which creates a real `order{orderStatus:"draft"}` and returns its documentId.
//   • cart survives   — items are NOT cleared on login; they stay in the tray, now carrying their
//                        orderDocumentId (booked=true). Reload keeps them (persisted). The draft
//                        order is the durable server-side truth; the tray is its live view.
//
// Never transacts against live: all network goes through same-origin Next route proxies
// (/api/auth/login, /api/marketplace/book) that target the LOCAL Strapi (STRAPI_URL).
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { gaItemFromCartItem, trackAddToCart, trackLogin, trackSignUp } from "@/lib/analytics-event";
import type { InlineResponse } from "@/lib/account-handoff";
import { inlineSsoLogin } from "@/lib/account-handoff";

export type CartItem = {
  id: string; // stable line identity: listingId when resolved, else mountain:supplier:productKey
  key: string; // data-product-key (per-supplier-page dedupe key)
  listingId: string | null; // BE product documentId (null when the BE hasn't resolved it — cart still works)
  title: string; // product name (Products_prodHeading)
  /**
   * WO-GF-001-P1B-S35 · F224 — the product's OWN details, read off the row that was added.
   *
   * Both optional and both honestly absent when the row has no such control: a rental row carries no
   * "Desired Time" select, so `startTime` is undefined there rather than defaulted to a plausible hour.
   * Optional also because the persisted store predates them — a line held in an existing session simply
   * expands without these rows instead of being back-filled with a guess.
   */
  description?: string; // Products_prodDesc — live's own product copy ("what it is")
  startTime?: string; // the selected "Desired Time" option (activities only)
  guestPrice: number | null;
  memberPrice: number | null;
  qty: number;
  startDate: string;
  endDate: string;
  mountain: string; // "whistler" in Phase-1
  supplier: string; // display name
  supplierSlug: string;
  booked: boolean; // true once a draft order exists for it (post sign-in reconcile)
  orderDocumentId: string | null;
  /**
   * WO-GF-001-P1B-S16 · F111 — which section of the cart this line belongs under.
   *
   * READ FROM LIVE'S OWN CLASSIFICATION, never guessed: an add always happens on a supplier page whose
   * path carries live's category segment (`/mountain-<m>/equipment-rentals/<slug>` vs
   * `/mountain-<m>/activities/<slug>`). That is the same split the marketplace seed used for
   * `product_category.categoryType`, and the same axis My Tickets groups on — so the cart and the
   * ticket view cannot disagree about what a product is.
   *
   * Optional because the persisted store predates it: a line held in an existing session simply has no
   * category and falls under OTHER rather than being silently filed as a rental.
   */
  category?: CartCategory;
  /**
   * WO-GF-001-P1B-S18 · F123 — which TOP-LEVEL section of the cart this line belongs under.
   *
   * F111 grouped the tray by `category` (RENTALS / ACTIVITIES / OTHER) and that was ONE LEVEL TOO DEEP.
   * The founder's live cart screenshot splits the cart by `MARKETPLACE` vs `TRANSPORT` — the same
   * top-level axis as the My Tickets CATEGORY tabs, where `category` is its SUB-tab axis. A cart holding
   * a zipline and two bus seats is 1 marketplace line + 2 transport lines; the old axis could not express
   * a transport line at all and would have filed the zipline under ACTIVITIES.
   *
   * `category` is NOT retired — it stays as the sub-axis (and is what My Tickets' sub-tabs read), so the
   * two views still name a product the same way. This adds the level above it.
   *
   * Optional because the persisted store predates it. A line held in an existing session was necessarily
   * added from a marketplace product page — every add path that existed before this field is a
   * marketplace one — so falling back to "marketplace" states what those lines ARE rather than guessing.
   */
  section?: CartSection;
  /** FWLR-025-R7 · L — the transport JOURNEY (one completed search) this leg belongs to, and its kind. Optional: only
   *  the design's legs carry it today; a line without it is its own journey. */
  tripId?: string;
  journey?: "round-trip" | "one-way";
  /**
   * WO-GF-001-P1B-S21 · F137 — the selected departure's own board detail, carried from the results
   * screen so the DETAILS "Ticket details" panel and the REVIEW order-summary can print live's
   * time-over-stop pairs (contract §4 + §6.3) without re-deriving them. Sourced entirely from the
   * `Departure` that was clicked; optional because only the funnel add path sets them and the persisted
   * store predates the fields — a marketplace line simply has none.
   */
  depTime?: string;
  depStop?: string;
  arrTime?: string;
  arrStop?: string;
  /**
   * WO-GF-001-P1B-S30 · F178 — THE SURFACE THIS LINE WAS ADDED FROM, pathname + search.
   *
   * `Edit` used to resolve through `editHref`, which is a SECTION-level rule: it returned the mountain's
   * marketplace hub or the mountain hub for the whole section, and fell back to `"/"` when the first
   * line had no mountain. That fallback is the founder's "Edit lands on the home page", and even when it
   * did not fire, a section hub is not the surface the traveller picked the product on — so nothing
   * could be pre-selected there.
   *
   * A line therefore records its own origin. Stamped in `addItem`, the ONE place every add path funnels
   * through, exactly as `category` (F111) and `section` (F123) are — so no add site is touched and no
   * path can be forgotten.
   *
   * The SEARCH string is kept deliberately: a transport line's source URL already encodes the whole
   * selection (`startDate`/`endDate`/`adult`/`child`/`senior`), so for that line type "return with the
   * selection intact" is satisfied by construction rather than by re-deriving anything.
   *
   * Optional because the persisted store predates it. A line held in an existing session has no origin
   * recorded, and `lineEditHref` falls back to the section rule for it rather than inventing one.
   */
  sourcePath?: string;
  /**
   * WO-GF-001-P1B-S30 · F177 — the supplier's logo, when the add site actually knows one.
   *
   * THE SOURCE EXISTS AND IS NOT READABLE YET, which is why this is optional rather than required:
   * `snowclub-be`'s `vendor` content-type already carries `logo` / `logoUrl` / `imageUrl`, but
   * `GET /api/vendors` returns **403 Forbidden** to the public role (probed 2026-07-26), so no surface
   * can read it. That is the same class as F1 (`api::order.order.find` ungranted) and the same one-line
   * fix: grant `api::vendor.vendor.find` in the BE permissions bootstrap. ROUTED, not worked around.
   *
   * Until it lands the cart renders an honest initials chip rather than a placeholder image — a slot
   * that fills itself the moment real data flows, and never a fabricated logo (the F81 rule).
   */
  supplierLogoUrl?: string;
  /**
   * WO-GF-001-P1B-S36 · card 3 (F261/F262/F263) — AN EXPLICIT LINE IDENTITY, for surfaces where the
   * thing being bought is a SLOT rather than a product.
   *
   * A marketplace line's identity is its product, so `lineId`'s mountain:supplier:key is exactly right
   * there and is untouched. A transport line's identity is the LEG: a trip has one departure and one
   * return, and which bus fills each is an ATTRIBUTE of that leg, not a different line. Keying it on the
   * departure id (`transport:<operator>:<stop>:<zone>:<time>:<leg>`) meant every re-pick minted a NEW
   * line — measured 2026-07-29 on the running build, signed out: three depart picks left three DEPARTURE
   * lines and a $284 total for one journey (F263's "two, two, two, two"), and the return leg could never
   * replace the departure it was meant to complete (F261).
   *
   * Optional, so nothing else changes: absent → `lineId` behaves exactly as before.
   */
  identity?: string;
  /** F-BATCH02 — the corridor slug the transport line was picked on, e.g. `vancouver-to-whistler`. */
  routeSlug?: string;
  /**
   * S36 · card 3 — which leg of the trip this line IS, and the ZONE it was priced in.
   *
   * Both are served facts about the selected departure (`Departure.zone` comes straight off the captured
   * stop's `zoneName`, which is the fare join's own key). They exist because the round-trip rule turns on
   * operator + zone, and the RETURN screen has to know what the OUTBOUND leg was in order to price the
   * remainder — reading it from the cart rather than from the URL means the price shown is a function of
   * what is actually in the basket, so the two can never disagree.
   */
  leg?: "depart" | "return";
  /**
   * WO-COMMERCE-BIND-001 §18b — THE CRITERIA THIS LEG WAS SEARCHED UNDER, carried from the results
   * screen so the booking submission can be built in live's own `CreateCartDTO` shape without
   * re-deriving anything. The engine prices a cart against `SearchCriteriaDTO`; zone codes and the pax
   * split exist ONLY on the search that produced the departure, so they ride on the line from the
   * moment it is added. Optional because the persisted store predates them — a line held in an older
   * session simply has none, and the submission REFUSES rather than inventing a route or a passenger
   * count (validateLegacyBooking).
   */
  searchCriteria?: {
    departureCode: string;
    arrivalCode: string;
    travelDate: string;
    returnDate?: string;
    adult?: number;
    child?: number;
    senior?: number;
    student?: boolean;
    noStore?: boolean;
    returnTrip?: boolean;
  };
  /** the schedule's own identifier — `CreateCartBookingJourneyDTO.itemIdentifier` */
  itemIdentifier?: string | number;
  zone?: string;
  /**
   * S36 · card 3 — which of the row's TWO published fare tables this leg was charged from (standard or
   * FLEXI). The remainder is `roundTrip − oneWay` off ONE table, so the return has to know which table
   * the departure actually used; without it a flexi departure and a standard return produce a total
   * that exists in neither (skylynx zone V: 35 + 10 = 45, vs a published 42 or 46).
   */
  flexi?: boolean;
};

export type CartCategory = "rentals" | "activities" | "other";

/** F123 — live's top-level cart axis. Drives the tray sections AND the per-section billing subtotals. */
export type CartSection = "marketplace" | "transport";

/**
 * live's category segment for the page an add happened on. Anything else (e.g. adding from
 * /deals-and-packages, which is not category-scoped) is OTHER — honest, not defaulted into a named tab.
 */
/**
 * F123 — the section a line added on `pathname` belongs to. Every add path that exists today lands on a
 * marketplace product surface, so marketplace is the truthful default rather than a guess; the transport
 * funnel passes `section: "transport"` explicitly at its own add site.
 */
export const sectionFromPath = (pathname: string): CartSection =>
  /\/(booking|bus-schedule)(\/|$)/.test(pathname) ? "transport" : "marketplace";

export const categoryFromPath = (pathname: string): CartCategory => {
  if (/\/equipment-rentals(\/|$)/.test(pathname) || /\/ski-rentals(\/|$)/.test(pathname)) {
    return "rentals";
  }
  if (/\/activities(\/|$)/.test(pathname)) return "activities";
  return "other";
};

// S24 · F156/F159 (W7/W10) — the signed-in profile, for pre-filling the funnel's Passenger + Customer
// forms. firstName/lastName/phone are loaded lazily from `/api/me` (loadProfile); they are optional so an
// older persisted session (email only) still works.
export type Session = {
  jwt: string | null;
  email: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  /**
   * CUSTOMER SSO — a COOKIE-BACKED session: the browser holds no token at all (`jwt` stays null, never a stand-in). The httpOnly
   * `sb_access` cookie, set by the SSO callback and verified by the server (/api/auth/session), is the credential, and the booking
   * routes read it server-side. Persisted only as this boolean + the email; it is re-verified on every load.
   */
  cookie?: boolean;
};

/**
 * Is anyone signed in — by a legacy bearer session OR a cookie-backed SSO session. Everything that used to ask `!!session.jwt` to
 * mean "signed in" should ask this; a cookie-backed session has no jwt to find, and none is ever fabricated to satisfy that test.
 */
export const isSignedIn = (session: Session): boolean => !!session.jwt || session.cookie === true;

/**
 * The only session shape browser storage may hold or hand back. `cookie: true` is an in-memory fact established by a fresh server
 * verification, never something to restore: it becomes a plain signed-out session (no email, no profile — an unverified identity is
 * not kept either). A legacy bearer session passes through unchanged; anything malformed becomes signed out.
 */
export function sanitizePersistedSession(raw: unknown): Session {
  const signedOut: Session = { jwt: null, email: null };
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return signedOut;
  const s = raw as Session;
  if (s.cookie !== true) return s;
  // A cookie marker is never kept. (A record that also carries a bearer is not a cookie session: the bearer is the legacy
  // session and stays, without the marker.)
  if (typeof s.jwt === "string" && s.jwt) {
    const { cookie: _marker, ...bearer } = s;
    void _marker;
    return bearer;
  }
  return signedOut;
}

export type BookResult = {
  ok: boolean;
  orderDocumentId?: string;
  total?: number;
  error?: string;
};

type CartState = {
  items: CartItem[];
  session: Session;
  open: boolean; // tray open
  authOpen: boolean; // login popup open (chrome sign-in — W6)
  /**
   * WO-GF-001-P1B-S21 · F134 — which face the auth popup opens on. The profile icon opens the guest MENU
   * first (live: Login/Sign Up · My Trips · …), but a guest clicking a MEMBER price on the funnel goes
   * STRAIGHT to the login form (live: the sign-in modal, no menu). Default "menu" preserves every existing
   * profile-icon trigger; the funnel sets "login" via openSignIn() before opening.
   */
  authView: "menu" | "login" | "signup";
  reconciling: boolean;
  lastError: string | null;
  hydrated: boolean;
  /**
   * CUSTOMER SSO public bootstrap: `null` = not asked yet, `true`/`false` = what the server's /api/auth/session said. Never
   * persisted. Off (`false`) means every legacy path below behaves exactly as it did before SSO existed.
   */
  sso: boolean | null;
  /**
   * A counter, never a credential: bumped each time a cookie-backed identity is adopted, replaced or dropped (and on sign-out). A
   * cookie session has no token whose change could be noticed, so the pricing context (design-pay `sessionIdentityOf`) uses this to
   * tell guest → member, one member → another, and logout → login apart. Never persisted.
   */
  identityGen: number;
  /** Ask the server once whether SSO is on (and adopt a verified cookie-backed session if it is). Resolves to the answer. */
  bootstrapSso: () => Promise<boolean>;
  /** Re-read the server-verified session (cookie-backed). Clears a stale cookie-backed session; never touches the cart. */
  hydrateSession: () => Promise<void>;
  /** First credential prompt inside the popup, on the page the visitor is on (same-origin, provider interaction API). */
  signInSso: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  /**
   * F123 — live's Driver Tip. A cart-level amount (live's own note says the tip applies to BOTH the
   * departure and the return leg, i.e. it is one tip for the transport booking, not a per-line charge).
   * Persisted with the items so it survives a reload exactly as the rest of the cart does.
   */
  driverTip: number;

  addItem: (item: Omit<CartItem, "id" | "booked" | "orderDocumentId">) => void;
  removeItem: (id: string) => void;
  setQty: (id: string, qty: number) => void;
  setDates: (id: string, startDate: string, endDate: string) => void;
  setDriverTip: (tip: number) => void;
  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  setAuthOpen: (open: boolean) => void;
  /** F134 — open the auth popup straight on the login form (the guest member-price path). */
  openSignIn: () => void;
  /** Youth spec v1.1: open the popup straight on NEW SIGN UP, whose first step is the age gate (components/site/SignupGate). */
  openSignUp: () => void;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ ok: boolean; error?: string }>;
  /**
   * F147 — create an account (name/email/phone/password), then behave exactly like a fresh sign-in.
   *
   * Card 86bb597gj adds `memberTypes`: the funnel's account-type selections, as stable taxonomy ids
   * (see `lib/member-types.ts`). They ride on the SAME call because the founder's Pages ruling makes
   * them facts about ONE personal identity, not a second account to create.
   */
  signUp: (fields: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    memberTypes?: string[];
    /** the age gate and agreement (components/site/SignupGate), required by /api/auth/register for every new account */
    birthMonth: number;
    birthYear: number;
    agreementAccepted: true;
    termsVersion: string;
    privacyVersion: string;
    membershipVersion: string;
  }) => Promise<{ ok: boolean; error?: string; status?: number }>;
  signOut: () => void;
  /** S24 · F156/F159 (W7/W10) — load the signed-in user's name/phone from `/api/me` for form pre-fill. */
  loadProfile: () => Promise<void>;
  reconcile: () => Promise<void>;
  /**
   * F129 — submit the cart's TRANSPORT lines as a HELD purchase. Returns the pending-acceptance order.
   * Separate from reconcile() on purpose: a marketplace line books against a Strapi listing by
   * documentId, whereas a bus seat has no listing at all (schedules live in the legacy API by design),
   * so the two write paths cannot be the same call.
   */
  submitTransport: () => Promise<{ ok: boolean; orderDocumentId?: string; error?: string }>;
  clear: () => void;
  count: () => number;
};

// The line identity: a resolved BE listing collapses to its documentId (so the same product added
// twice upserts); an unresolved item falls back to mountain:supplier:productKey (still unique per page).
const lineId = (i: {
  identity?: string;
  listingId: string | null;
  mountain: string;
  supplierSlug: string;
  key: string;
}) => i.identity || i.listingId || `${i.mountain}:${i.supplierSlug}:${i.key}`;

// Book one held item against the LOCAL BE via the same-origin proxy. Resilient: any failure returns
// {ok:false} and leaves the item in the cart (booked stays false) — the cart never loses data.
// S22 · F124 — the item's picked USE date (startDate/endDate) + optional use TIME (depTime) now ride
// along so the BE can store them; a line with no date books exactly as before (fields omitted → null).
async function bookItem(
  jwt: string | null,
  item: Pick<CartItem, "listingId" | "qty" | "startDate" | "endDate" | "depTime">,
): Promise<BookResult> {
  try {
    const res = await fetch("/api/marketplace/book", {
      method: "POST",
      // A legacy bearer session sends its Authorization exactly as before; a cookie-backed SSO session sends none and the
      // same-origin httpOnly cookie is read server-side by the booking route.
      headers: {
        "Content-Type": "application/json",
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      },
      credentials: "same-origin",
      body: JSON.stringify({
        listingDocumentId: item.listingId,
        quantity: item.qty,
        startDate: item.startDate || undefined,
        endDate: item.endDate || undefined,
        useTime: item.depTime || undefined,
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok)
      return { ok: false, error: json?.error?.message || `book ${res.status}` };
    return {
      ok: true,
      orderDocumentId: json.orderDocumentId,
      total: json.total,
    };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "network" };
  }
}

/** A sign-up refusal as plain text: the answer's non-empty `error` string, else its non-empty `error.message`, else a plain failure. */
function signUpRefusal(error: unknown): string {
  if (typeof error === "string" && error !== "") return error;
  if (typeof error === "object" && error !== null) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message !== "") return message;
  }
  return "Sign-up failed";
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      session: { jwt: null, email: null },
      open: false,
      authOpen: false,
      authView: "menu",
      reconciling: false,
      lastError: null,
      hydrated: false,
      sso: null,
      identityGen: 0,
      driverTip: 0,

      // The one place the server's verified session answer is applied. SSO off: nothing is adopted (the legacy bearer session
      // is the only session there). SSO on: a verified cookie becomes the cookie-backed session (no jwt, ever); a missing or
      // rejected one drops a stale cookie-backed session — the cart itself is never touched. A legacy bearer session is left alone.
      bootstrapSso: async () => {
        if (get().sso !== null) return get().sso === true;
        await get().hydrateSession();
        return get().sso === true;
      },

      hydrateSession: async () => {
        try {
          const res = await fetch("/api/auth/session", { cache: "no-store", credentials: "same-origin" });
          const dto = (await res.json().catch(() => null)) as {
            signedIn?: unknown;
            sso?: unknown;
            email?: unknown;
            profile?: { firstName?: unknown; lastName?: unknown; phone?: unknown };
          } | null;
          if (!res.ok || !dto) return;
          const sso = dto.sso === true;
          set({ sso });
          if (!sso) return;
          const { session } = get();
          if (dto.signedIn === true && typeof dto.email === "string" && dto.email) {
            if (session.jwt) return;
            const text = (v: unknown) => (typeof v === "string" ? v : null);
            // the same verified identity re-read is not a new identity; a first adoption or a different member is
            const changed = !(session.cookie === true && session.email === dto.email);
            set({
              identityGen: changed ? get().identityGen + 1 : get().identityGen,
              session: {
                jwt: null,
                cookie: true,
                email: dto.email,
                firstName: text(dto.profile?.firstName),
                lastName: text(dto.profile?.lastName),
                phone: text(dto.profile?.phone),
              },
            });
          } else if (session.cookie) {
            set({ identityGen: get().identityGen + 1, session: { jwt: null, email: null } });
          }
        } catch {
          /* the server could not be asked: keep the current state, change nothing */
        }
      },

      signInSso: async (email, password) => {
        const fail = (error: string) => {
          set({ lastError: error });
          return { ok: false, error };
        };
        if (typeof window === "undefined") return fail("Sign-in is unavailable here.");
        const result = await inlineSsoLogin({
          fetch: (url, init) => fetch(url, init) as unknown as Promise<InlineResponse>,
          origin: window.location.origin,
          returnTo: `${window.location.pathname}${window.location.search}`,
          identifier: email,
          password,
        });
        if (!result.ok) return fail(result.error);
        // A finished redirect chain is not a session. Only the server-verified read of the persisted cookie establishes one, so a
        // sign-in the browser did not keep never proceeds (the same rule the AuthProvider's persisted-session guard enforces).
        await get().hydrateSession();
        if (!isSignedIn(get().session)) return fail("We couldn't confirm your sign-in. Please try again.");
        set({ lastError: null });
        await resolveAgeMode(); // the server band before any login measurement: youth -> essential-only
        syncAdPixels();
        trackLogin("email");
        await get().reconcile(); // the same founder rule as the legacy signIn: the held cart survives and is booked
        return { ok: true };
      },

      addItem: (raw) => {
        const id = lineId(raw);
        const item: CartItem = {
          ...raw,
          id,
          booked: false,
          orderDocumentId: null,
          // F111 — stamped here, in the ONE place every add funnels through, so both add paths (the
          // detail booking widget and the quick-view Continue) get it without either being touched.
          category:
            raw.category ??
            (typeof window === "undefined"
              ? "other"
              : categoryFromPath(window.location.pathname)),
          // F123 — stamped in the SAME one place as `category`, so both add paths (the detail booking
          // widget and the quick-view Continue) inherit it without either being touched, and so the
          // transport funnel only has to pass its own section explicitly.
          section:
            raw.section ??
            (typeof window === "undefined"
              ? "marketplace"
              : sectionFromPath(window.location.pathname)),
          // F178 — stamped in the SAME one place as `category` and `section`, for the same reason: every
          // add path funnels through here, so none can be missed and no add site has to be edited.
          // pathname + search, because for a transport line the search string IS the selection.
          sourcePath:
            raw.sourcePath ??
            (typeof window === "undefined"
              ? undefined
              : `${window.location.pathname}${window.location.search}`),
        };
        // GA4 add_to_cart is for a line that was not in the cart. Re-picking or re-adding an existing line
        // (an upsert, or a bus swapped on the same leg) is not a new add and reports nothing.
        const isNewLine = !get().items.some((i) => i.id === id);
        set((s) => {
          const existing = s.items.find((i) => i.id === id);
          // S36 · card 3 — a REPLACEMENT is not a re-add. When an identity-keyed line is filled by a
          // DIFFERENT selection (`key` is the departure id, so it changes iff the bus changed), the
          // booking that was made against the old selection no longer describes this line, and keeping
          // `booked` would leave the tray claiming a server-side order for a bus the traveller just
          // swapped out. Same reasoning `setQty` already applies to a quantity change.
          const replaced = !!existing && existing.key !== item.key;
          const items = existing
            ? s.items.map((i) =>
                i.id === id
                  ? {
                      ...i,
                      ...item,
                      booked: replaced ? false : i.booked,
                      orderDocumentId: replaced ? null : i.orderDocumentId,
                    }
                  : i,
              )
            : [...s.items, item];
          return { items, open: true };
        });
        if (isNewLine) trackAddToCart(gaItemFromCartItem(item));
        // If already signed in, book immediately (signed-in add = server truth right away — live parity).
        const { session } = get();
        if (isSignedIn(session) && raw.listingId) void get().reconcile();
      },

      removeItem: (id) =>
        set((s) => ({ items: s.items.filter((i) => i.id !== id) })),

      setQty: (id, qty) =>
        set((s) => ({
          items: s.items.map((i) =>
            i.id === id
              ? {
                  ...i,
                  qty: Math.max(1, Math.min(10, qty)),
                  booked: false,
                  orderDocumentId: null,
                }
              : i,
          ),
        })),

      // F123 — clamped at 0 (a negative tip is not a thing) and rounded to cents. No upper bound: live's
      // input is free-form and capping it would invent a rule live does not state.
      /**
       * FOUNDER-WALK-QA-STAGING-007 · D2 — EDIT A LINE'S DATES IN PLACE.
       *
       * The cart could always re-plan a QUANTITY (`setQty`, wired to the page's −/+) but never a DATE:
       * this store had no date action at all, so both the tray and the page printed
       * `For {startDate} → {endDate}` as dead text and the only way to change a booked date was to
       * remove the line and add it again from the product page.
       *
       * INVALIDATION IS `setQty`'s, deliberately identical. A re-dated line is not the line that was
       * quoted or booked — the draft order behind it was created against the OLD dates — so `booked`
       * and `orderDocumentId` are cleared exactly as a quantity change clears them, and the next
       * `reconcile()` re-books it. Unconditional, again exactly as `setQty`: re-submitting the same
       * dates costs one re-book and never leaves a stale order id claiming to describe this line.
       *
       * A BACKWARDS RANGE IS REFUSED RATHER THAN STORED. `dateGuard` (cart-billing) is the same rule
       * the add path gates on; a cart edit cannot be allowed to create the state the add path forbids.
       * Refusal is a no-op on the whole store, so a rejected edit cannot invalidate a booked line as a
       * side effect — the surface re-renders the line's existing dates and says why.
       */
      setDates: (id, startDate, endDate) =>
        set((s) => {
          const start = (startDate || "").trim();
          const end = (endDate || "").trim();
          if (start && end && end < start) return {}; // backwards range — refuse, change nothing
          if (!s.items.some((i) => i.id === id)) return {};
          return {
            items: s.items.map((i) =>
              i.id === id
                ? { ...i, startDate: start, endDate: end, booked: false, orderDocumentId: null }
                : i,
            ),
          };
        }),

      setDriverTip: (tip) =>
        set({ driverTip: Number.isFinite(tip) ? Math.max(0, Math.round(tip * 100) / 100) : 0 }),

      setOpen: (open) => set({ open }),
      toggleOpen: () => set((s) => ({ open: !s.open })),
      // Closing always resets the view to the guest menu, so the next profile-icon click starts there
      // again (F134) — only openSignIn() opts into the login-first face.
      setAuthOpen: (authOpen) => set(authOpen ? { authOpen } : { authOpen: false, authView: "menu" }),
      openSignIn: () => set({ authOpen: true, authView: "login" }),
      // Youth spec v1.1: every sign-up entry opens the popup on its gated NEW SIGN UP (age step first).
      openSignUp: () => set({ authOpen: true, authView: "signup" }),

      signIn: async (email, password) => {
        try {
          const res = await fetch("/api/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ identifier: email, password }),
          });
          const json = await res.json().catch(() => ({}));
          if (!res.ok || !json.jwt) {
            const error = json?.error || "Invalid identifier or password";
            set({ lastError: error });
            return { ok: false, error };
          }
          set({
            session: { jwt: json.jwt, email: json.email || email },
            lastError: null,
          });
          await resolveAgeMode(); // the server band before any login measurement: youth -> essential-only
          syncAdPixels();
          trackLogin("email");
          await get().reconcile(); // THE migration: held cart -> draft order(s)
          return { ok: true };
        } catch (e) {
          const error = e instanceof Error ? e.message : "network";
          set({ lastError: error });
          return { ok: false, error };
        }
      },

      // F147 — sign-up. On success the account exists and the user is signed in; the held cart reconciles
      // to a draft order exactly as sign-in does (same founder rule). Errors surface the BE's message.
      signUp: async (fields) => {
        try {
          const res = await fetch("/api/auth/register", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fields),
          });
          // The proxy's answer, narrowed from `unknown`: success only with a real session token (a 2xx without one is still a failure),
          // and a refusal keeps the backend's status (409 re-read, 403 under 13) with its own message as plain text, never an object.
          const raw: unknown = await res.json().catch(() => null);
          const body: Record<string, unknown> = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
          const jwt = typeof body.jwt === "string" ? body.jwt : "";
          if (!res.ok || jwt === "") {
            const error = signUpRefusal(body.error);
            set({ lastError: error });
            return { ok: false, error, status: res.status };
          }
          set({ session: { jwt, email: typeof body.email === "string" && body.email !== "" ? body.email : fields.email }, lastError: null });
          // The server's band (the register answer set the youth flag): youth -> essential-only before the sign-up event could send.
          await resolveAgeMode();
          syncAdPixels();
          trackSignUp("email");
          await get().reconcile();
          return { ok: true };
        } catch (e) {
          const error = e instanceof Error ? e.message : "network";
          set({ lastError: error });
          return { ok: false, error };
        }
      },

      // F242 — sign-in writes the shared `sb_access` cookie the accounts app reads, so signing out has to
      // clear it too. Fire-and-forget: the local session drops immediately (this stays a synchronous
      // action) and the cookie clear is a same-origin POST that cannot fail visibly.
      //
      // SSO: a cookie-backed session also has the issuer's own session behind it. The logout answer names the same-origin RP-initiated
      // logout (a fixed relative path); following it clears the cookie again, ends the provider session and returns here. Feature
      // off, the answer has no such field and this is exactly the previous behaviour.
      signOut: () => {
        const wasCookie = get().session.cookie === true;
        set({ identityGen: get().identityGen + 1, session: { jwt: null, email: null } });
        void fetch("/api/auth/logout", { method: "POST" })
          .then(async (res) => {
            if (!wasCookie || typeof window === "undefined") return;
            const body = (await res.json().catch(() => null)) as { endSessionUrl?: unknown } | null;
            if (body?.endSessionUrl === "/api/auth/sso/logout") {
              window.location.assign(`/api/auth/sso/logout?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`);
            }
          })
          .catch(() => undefined);
      },

      // S24 · F156/F159 (W7/W10) — pull the signed-in user's profile so the funnel forms pre-fill. No-op
      // for a guest; failures are swallowed (the forms simply stay empty rather than blocking the funnel).
      loadProfile: async () => {
        const { session } = get();
        // A cookie-backed session already carries its profile from the verified /api/auth/session read (no bearer to send here).
        if (!session.jwt) return;
        try {
          const res = await fetch("/api/me", { headers: { Authorization: `Bearer ${session.jwt}` } });
          if (!res.ok) return;
          const p = (await res.json()) as { email?: string | null; firstName?: string | null; lastName?: string | null; phone?: string | null };
          // The answer belongs to the token that asked: signed out or signed in as someone else meanwhile, it is not this session's.
          if (get().session.jwt !== session.jwt) return;
          set((s) => ({
            session: {
              ...s.session,
              email: p.email ?? s.session.email,
              firstName: p.firstName ?? null,
              lastName: p.lastName ?? null,
              phone: p.phone ?? null,
            },
          }));
        } catch {
          /* profile fetch failed → forms stay empty, funnel unaffected */
        }
      },

      // Reconcile the session cart to the BE: book every unbooked item that has a resolved listingId.
      // Items REMAIN in the cart (cart survives); they gain booked=true + their orderDocumentId.
      reconcile: async () => {
        const { session, items } = get();
        if (!isSignedIn(session)) return;
        set({ reconciling: true });
        let lastError: string | null = null;
        for (const item of items) {
          if (item.booked || !item.listingId) continue;
          const r = await bookItem(session.jwt, item);
          if (r.ok) {
            set((s) => ({
              items: s.items.map((i) =>
                i.id === item.id
                  ? {
                      ...i,
                      booked: true,
                      orderDocumentId: r.orderDocumentId || null,
                    }
                  : i,
              ),
            }));
          } else {
            lastError = r.error || "book failed";
          }
        }
        set({ reconciling: false, lastError });
      },

      // F129 — the transport half of the write path. Books every UNBOOKED transport line in one request
      // (they are one trip, so they belong on one order the operator accepts as a whole) and, on
      // success, marks exactly those lines booked against the returned pending-acceptance order.
      // Marketplace lines are untouched — their draft-order path is unchanged.
      submitTransport: async () => {
        const { session, items, driverTip } = get();
        if (!isSignedIn(session)) return { ok: false, error: "Log in to book" };
        const pending = items.filter(
          (i) => (i.section ?? "marketplace") === "transport" && !i.booked,
        );
        if (pending.length === 0) return { ok: false, error: "No transport lines to submit" };
        set({ reconciling: true });
        try {
          const res = await fetch("/api/marketplace/book-transport", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(session.jwt ? { Authorization: `Bearer ${session.jwt}` } : {}),
            },
            credentials: "same-origin",
            body: JSON.stringify({
              lines: pending.map((i) => ({
                title: i.title,
                supplier: i.supplier,
                quantity: i.qty,
                // the charged basis, same as the tray displays — never a re-derived number
                price: i.guestPrice ?? 0,
                startDate: i.startDate,
                // F124 — the board time the funnel stamped on the line (transport use TIME)
                useTime: i.depTime,
              })),
              driverTip,
            }),
          });
          const json = await res.json().catch(() => ({}));
          if (!res.ok) {
            const error = json?.error?.message || json?.error || `book ${res.status}`;
            set({ reconciling: false, lastError: error });
            return { ok: false, error };
          }
          const orderDocumentId: string | undefined = json.orderDocumentId;
          const ids = new Set(pending.map((p) => p.id));
          set((s) => ({
            reconciling: false,
            lastError: null,
            items: s.items.map((i) =>
              ids.has(i.id)
                ? { ...i, booked: true, orderDocumentId: orderDocumentId ?? null }
                : i,
            ),
          }));
          return { ok: true, orderDocumentId };
        } catch (e) {
          const error = e instanceof Error ? e.message : "network";
          set({ reconciling: false, lastError: error });
          return { ok: false, error };
        }
      },

      clear: () => set({ items: [] }),
      count: () => get().items.length,
    }),
    {
      name: "sc-cart",
      // Persist the durable session state (items + auth) so the cart survives reload AND survives the
      // sign-in transition — the whole point of the founder rule. Transient UI (open/reconciling) is not.
      //
      // A cookie-backed SSO session is NEVER persisted as authenticated: browser storage is not evidence of a session, and
      // `isSignedIn` treats `cookie: true` as signed in. Only the server (/api/auth/session) can establish it, in memory, after a
      // fresh verification — so it is written out as signed-out, and a bearer (legacy) session persists exactly as before.
      partialize: (s) => ({
        items: s.items,
        session: sanitizePersistedSession(s.session),
        driverTip: s.driverTip, // F123 — part of the order being built, so it survives reload like the lines
      }),
      // The same rule on the way IN, through the real merge: records written before this fix may still carry `cookie: true`, and
      // storage can hold anything. Whatever is restored, a cookie marker is dropped; everything else (the held cart, tip, a legacy
      // bearer session) is restored as it was.
      merge: (persisted, current) => {
        const stored = (persisted && typeof persisted === "object" ? persisted : {}) as Partial<CartState>;
        return { ...current, ...stored, session: sanitizePersistedSession(stored.session ?? current.session) };
      },
      onRehydrateStorage: () => (state) => {
        if (state) state.hydrated = true;
      },
    },
  ),
);

// Expose the store for the port harness (behavior.mjs drives + asserts the founder run deterministically).
if (typeof window !== "undefined") {
  (window as unknown as { __cart?: typeof useCartStore }).__cart = useCartStore;
}
